import colorVars from "@/assets/styles/colorVars"
import { Spacer } from "@/components/spacer"
import anime, { AnimeTimelineInstance } from "animejs"
import { isNotEmpty } from "ramda-extension"
import React, { FC, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"

import { ISequenceTiming, Omnichannel } from "./AnimationPlayer"
import { getChatImage, getWordLength } from "./animationHelpers"
// Animation timings
const timing = {
  aiMessageRevealDelay: 110,
  aiThinkingMessageGlowDuration: 3800,
  aiThinkingTime: 2800,
  delayBetweenMessages: 760,
  messageRevealDelay: 380,
}

// Type definitions
export interface IChatScene {
  chatStarterDate: string
  containerRef: any
  identity: {
    agentName?: string
    chatImage: string
    chatTitle: string
    userName?: string
  }
  isAnimMultiScene?: boolean
  messageEntries: Array<MessageEntryProps>
  omnichannel?: Omnichannel
  sequenceTiming: ISequenceTiming
}

export interface MetadataProps {
  cta?: string
  description?: string
  image?: string
  isThinking?: boolean
  price?: string
  title?: string
  type?: "contact" | "product"
}

export interface MessageEntryProps {
  directive?: DirectiveProps
  message?: string
  metadata?: MetadataProps
  newDate?: string
  sender?: "agent" | "user"
  smallerFont?: boolean
}

export interface DirectiveProps {
  position?: {
    left?: number
    top?: number
  }
  text?: string
}

// Chat components
const ChatContainer = styled.div``
const ChatWrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #060816;
`
const Content = styled.div`
  padding-bottom: var(--device-ui-height);
`
const DateIndicator = styled.span`
  display: flex;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 21px;
  color: #949494;
  text-transform: uppercase;
`

// Chat: Messages
const MessageText = styled.span<MessageEntryProps>`
  color: white;

  ${(props) =>
    props.smallerFont
      ? css`
          font-size: 13px;
          line-height: 18px;
        `
      : css`
          font-size: 14px;
          line-height: 20px;
        `}
`
const Thinking = styled.div`
  position: absolute;
  bottom: 11px;
  left: 58px;
  z-index: 2;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #f4f4f4;
`
const DotFlashingAnim = styled.div`
  /* Adapted from https://codepen.io/nzbin/pen/GGrXbp */
  position: absolute;
  top: 6px;
  left: -36px;
  width: 6px;
  height: 6px;
  color: white;
  background-color: white;
  border-radius: 5px;
  animation: dot-flashing 0.5s infinite linear alternate;
  animation-delay: 0.25s;

  &::before,
  &::after {
    position: absolute;
    top: 0;
    display: inline-block;
    content: "";
  }

  &::before {
    left: -10px;
    width: 6px;
    height: 6px;
    color: white;
    background-color: white;
    border-radius: 5px;
    animation: dot-flashing 0.5s infinite alternate;
    animation-delay: 0s;
  }

  &::after {
    left: 10px;
    width: 6px;
    height: 6px;
    color: white;
    background-color: white;
    border-radius: 5px;
    animation: dot-flashing 0.5s infinite alternate;
    animation-delay: 0.5s;
  }

  @keyframes dot-flashing {
    0% {
      background-color: white;
    }
    50%,
    100% {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
`
const MessageBubble = styled.div<MetadataProps>`
  display: inline-flex;
  min-width: 50px;
  max-width: 90%;
  min-height: 40px;
  padding: 10px 14px;
  transition: all 500ms ease;

  ${({ isThinking }) =>
    isThinking &&
    css`
      position: relative;
      z-index: 0;
      overflow: hidden;
      border-radius: 0 8px 8px;

      &::before {
        position: absolute;
        top: -350%;
        left: -150%;
        z-index: -2;
        width: 400%;
        aspect-ratio: 1 / 1;
        content: "";
        background-color: #1f212e;
        background-image: linear-gradient(
          rgba(255, 39, 104, 0) 35%,
          rgba(255, 78, 131, 1) 60%
        );
        background-repeat: no-repeat;
        background-size: 100%;
        transform-origin: 50% 50%;
        animation: rotate 3000ms linear infinite;
      }

      &::after {
        position: absolute;
        top: 1px;
        left: 1px;
        z-index: -1;
        width: calc(100% - 2px);
        height: calc(100% - 2px);
        content: "";
        background: #1f212e;
        border-radius: 0 7px 7px;
      }

      @keyframes rotate {
        100% {
          transform: rotate(1turn);
        }
      }
    `}
`
const StyledMessage = styled.div<MessageEntryProps>`
  display: flex;
  flex-flow: row wrap;
  min-width: 65px;
  margin: 4px 0;
  transition: width 250ms ease, min-width 250ms ease;

  &.fade-out {
    ${MessageBubble} {
      &::before {
        opacity: 1;
        animation: rotate 3000ms linear infinite,
          fade-out
            ${timing.aiThinkingTime +
              timing.aiThinkingMessageGlowDuration / 1.5 ?? 2000}ms
            ease forwards;

        @keyframes fade-out {
          20% {
            opacity: 1;
          }
          45% {
            opacity: 0;
          }
        }
      }
    }
  }

  ${({ sender }) =>
    sender === "user"
      ? css`
          justify-content: flex-end;
          transform-origin: 100% 0%;

          ${MessageBubble} {
            background: rgba(124, 107, 224, 0.1);
            border-radius: 8px 0 8px 8px;
          }
        `
      : css`
          transform-origin: 0% 0%;

          ${MessageBubble},
          .embed {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 0 8px 8px 8px;
          }
        `}
`
const Author = styled.span<any>`
  flex: 1 0 100%;
  margin: 6px 0 6px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.1px;

  ${({ sender }) =>
    sender === "user"
      ? css`
          color: #7c6be0;
          text-align: right;
        `
      : css`
          color: #f4f4f4;
        `}
`

// Metadata: Embedded Product
const Embed = styled.div``
const EmbedContainer = styled.div`
  display: flex;
  overflow: hidden;
  border-radius: 6px;
`
const EmbedImage = styled.img`
  width: 76px;
  object-fit: cover;
`
const EmbedData = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  justify-content: center;
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  line-height: 20px;
  color: white;
  background: rgba(244, 244, 244, 0.1);
`
const EmbedTitle = styled.span`
  font-weight: 600;
`
const EmbedDescription = styled.span``
const EmbedPrice = styled.span``

// Metadata: Contact Card
const ContactCard = styled.div`
  padding: 4px 0;
`
const ContactCardContainer = styled.div`
  display: flex;
  gap: 0 10px;
`
const ContactCardImage = styled.img`
  width: 35px;
  height: 35px;
  border-radius: 50px;
`
const ContactCardData = styled.div`
  display: flex;
  flex-flow: column;
`
const ContactCardTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: white;
`
const ContactCardDescription = styled.span`
  font-size: 10px;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.5);
`
const ContactCardCta = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% + 28px);
  height: 27px;
  margin: 12px -14px -13px;
  font-size: 12px;
  color: ${colorVars.blue.main};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

// AI Agent Directives
const Directive = styled.div<any>`
  position: fixed;
  top: 0;
  left: 16px;
  z-index: 8;
  display: block;
  max-width: 170px;
  padding: 6px 12px;
  font-size: 12px;
  line-height: 18px;
  color: white;
  background: linear-gradient(124deg, #ff4e83, #ff2768);
  border-radius: 8px;
  opacity: 0;
  transform-origin: 0% 0%;
`

export const Chat: FC<IChatScene> = ({
  chatStarterDate,
  containerRef,
  identity,
  messageEntries,
  omnichannel,
  sequenceTiming,
}) => {
  // Refs
  const timelineRef = useRef<AnimeTimelineInstance>()
  const contentRef = useRef<HTMLDivElement>(null)
  const directiveRef = useRef<HTMLElement>(null)

  // States
  const [delayedThinking, setDelayedThinking] = useState<any>([])
  const [displayedTexts, setDisplayedTexts] = useState<any>([]) // Set initial thinking status for AI messages
  const [directiveText, setDirectiveText] = useState<null | string>(null) // Handle directives for AI-generated messages
  const [messageHeights, setMessageHeights] = useState<number[]>([]) // Keep a state of computed message heights (for auto-scrolling)

  // Timer references (for cleanup during component unmount)
  let wordRevealTimeout: any
  let aiDisplayTextTimeout: any
  let aiDelayThinkingTimeout: any
  let finalMessageTimeout: any

  // Helper Functions
  // Helper function to handle directive visibility and transitions
  const showAnimatedDirective = (visible: boolean, messageEl?: HTMLElement) => {
    let elemRect, yPos

    if (messageEl?.parentElement) {
      elemRect = messageEl.getBoundingClientRect()
      yPos = elemRect.y / 2 + 80
    }

    anime({
      duration: timing.messageRevealDelay,
      easing: "easeOutCubic",
      keyframes: [
        { left: omnichannel === "web" ? 145 : 16, top: yPos },
        {
          opacity: visible ? [0, 1] : [1, 0],
          scale: visible ? [0.75, 1] : [1, 0.75],
        },
      ],
      targets: directiveRef.current,
    })
  }

  const handleDirective = (text?: string, messageEl?: HTMLElement) => {
    if (text) {
      setDirectiveText(text)
      if (directiveRef.current) {
        showAnimatedDirective(true, messageEl)
      }
    } else {
      if (directiveRef.current) {
        showAnimatedDirective(false)
      }
    }
  }

  // Helper function to gradually reveal a message
  const revealMessage = (index: number, message: string) => {
    // Fade out directive
    if (messageEntries[index].directive) {
      showAnimatedDirective(false)
    }

    const words = message.split(" ")

    let tempMessage = ""
    words.forEach((word, wordIndex) => {
      wordRevealTimeout = setTimeout(() => {
        tempMessage += word + " "
        setDisplayedTexts((prevTexts: any) => {
          const updatedTexts = [...prevTexts]
          updatedTexts[index] = tempMessage.trimEnd()
          return updatedTexts
        })

        // Check if it's the last word of the last message
        if (
          wordIndex === words.length - 1 &&
          index === messageEntries.length - 1
        ) {
          // If so, we don't call play()
          return
        }

        if (wordIndex === words.length - 1) {
          timelineRef.current?.play()

          const messageEl = document.getElementById(`message-${index}`)
          if (messageEl) {
            setMessageHeights((prevHeights) => {
              const updatedHeights = [...prevHeights]
              updatedHeights[index] = messageEl.offsetHeight
              return updatedHeights
            })
          }
        }
      }, timing.aiMessageRevealDelay * wordIndex)
    })
  }

  // Handle automatic scrolling when messages fly by
  const autoScroll = (messageEl: HTMLElement, index: number) => {
    let scrollOffset = 0

    if (contentRef.current) {
      const messageHeight = messageHeights[index] || messageEl.offsetHeight
      if (
        messageEl.offsetTop + messageHeight >=
        contentRef.current.scrollTop + containerRef.clientHeight / 1.3
      ) {
        scrollOffset =
          messageEl.offsetTop + messageHeight - containerRef.clientHeight / 1.66
      } else {
        scrollOffset = 5
      }

      index !== 0 &&
        containerRef.scrollTo({
          behavior: "smooth",
          top: scrollOffset,
        })
    }
  }

  // Handle all effects
  let tl: AnimeTimelineInstance | undefined

  useEffect(() => {
    tl = anime.timeline({
      autoplay: false,
      complete: () => tl?.pause(),
      easing: "easeOutCubic",
      loop: false,
    })

    timelineRef.current = tl

    setDisplayedTexts(
      messageEntries.map((entry: any) =>
        entry.sender !== "user" ? "thinking-state" : entry.message,
      ),
    )
    setDelayedThinking(
      messageEntries.map((entry: any) =>
        entry.sender !== "user" ? true : false,
      ),
    )

    // Setup animation timeline
    if (!contentRef.current || !timelineRef.current) return

    messageEntries.forEach((entry: any, index: number) => {
      const messageEl = document.getElementById(`message-${index}`)

      if (messageEl && timelineRef.current) {
        timelineRef.current.add({
          begin: () => autoScroll(messageEl, index),
          complete: () => {
            if (entry.directive && entry.sender !== "user") {
              handleDirective(entry.directive.text, messageEl)
            }

            if (entry.sender !== "user" && timelineRef.current) {
              timelineRef.current.pause()
              // After the AI "thinks", reveal the message
              aiDisplayTextTimeout = setTimeout(() => {
                setDisplayedTexts((prevTexts: any) => {
                  const updatedTexts = [...prevTexts]
                  updatedTexts[index] = "" // Clear the "thinking-state"
                  return updatedTexts
                })

                // Manually fade out message effect via CSS class
                messageEl.className += " fade-out"

                revealMessage(index, entry.message ?? "")

                // Add a delay to change the delayedThinking state after 3 seconds
                aiDelayThinkingTimeout = setTimeout(() => {
                  setDelayedThinking((prevThinking: any) => {
                    const updatedThinking = [...prevThinking]
                    updatedThinking[index] = false
                    return updatedThinking
                  })
                }, timing.aiThinkingMessageGlowDuration)
              }, timing.aiThinkingTime)
            } else {
              // For user messages, set the height immediately
              setMessageHeights((prevHeights) => {
                const updatedHeights = [...prevHeights]
                updatedHeights[index] = messageEl.offsetHeight
                return updatedHeights
              })
            }

            const finalMessageTiming =
              sequenceTiming.timeBetweenFullReplay +
              timing.aiMessageRevealDelay * getWordLength(entry.message)

            // On last message, check for Anime.js timeline completion promise and dispatch event
            if (index === messageEntries.length - 1) {
              timelineRef.current?.finished.then(() => {
                finalMessageTimeout = setTimeout(() => {
                  if (omnichannel === "sms") {
                    window.dispatchEvent(new CustomEvent("pxmChatAnimComplete"))
                  } else {
                    window.dispatchEvent(
                      new CustomEvent("pxmWebChatAnimComplete"),
                    )
                  }
                }, finalMessageTiming)
              })
            }
          },
          delay:
            index === 0
              ? timing.messageRevealDelay
              : timing.delayBetweenMessages,
          duration: timing.messageRevealDelay,
          opacity: [0, 1],
          scale: [0.75, 1],
          targets: messageEl,
        })
      }
    })

    // Play the timeline when event is fired
    const playTimeline = () => {
      timelineRef.current?.play()
    }

    window.addEventListener("pxmStartChatAnim", playTimeline)
    // Cleanup
    return () => {
      // Remove event listeners
      window.removeEventListener("pxmStartChatAnim", playTimeline)

      // Clear timeouts
      clearTimeout(wordRevealTimeout)
      clearTimeout(aiDisplayTextTimeout)
      clearTimeout(aiDelayThinkingTimeout)
      clearTimeout(finalMessageTimeout)

      // Clear timeline
      tl?.pause()
      tl = undefined
      timelineRef.current = undefined
    }
  }, [])

  return (
    <ChatContainer key={omnichannel}>
      <Directive ref={directiveRef}>{directiveText}</Directive>
      <ChatWrapper>
        <Content ref={contentRef}>
          {omnichannel === "sms" && <Spacer height={{ default: "16px" }} />}
          <DateIndicator>{chatStarterDate}</DateIndicator>
          {messageEntries?.map((entry, index) => {
            const { metadata, sender } = entry
            const prevAuthor = messageEntries[index - 1]?.sender ?? ""
            const isThinking = displayedTexts[index] === "thinking-state"

            const renderContent = () => {
              if (metadata && isNotEmpty(metadata)) {
                if (metadata.type === "product") {
                  return (
                    <Embed>
                      <EmbedContainer>
                        <EmbedImage alt={metadata.title} src={metadata.image} />
                        <EmbedData>
                          <EmbedTitle>{metadata.title}</EmbedTitle>
                          <EmbedDescription>
                            {metadata.description}
                          </EmbedDescription>
                          <EmbedPrice>{metadata.price}</EmbedPrice>
                        </EmbedData>
                      </EmbedContainer>
                    </Embed>
                  )
                } else {
                  return (
                    <ContactCard>
                      <ContactCardContainer>
                        <ContactCardImage
                          alt={metadata.title}
                          height="42"
                          src={getChatImage(identity.chatImage)}
                          width="42"
                        />
                        <ContactCardData>
                          <ContactCardTitle>{metadata.title}</ContactCardTitle>
                          <ContactCardDescription>
                            {metadata.description}
                          </ContactCardDescription>
                        </ContactCardData>
                      </ContactCardContainer>
                      {metadata.cta && (
                        <ContactCardCta>{metadata.cta}</ContactCardCta>
                      )}
                    </ContactCard>
                  )
                }
              } else {
                return (
                  <MessageText smallerFont={omnichannel === "web"}>
                    {displayedTexts[index]}
                  </MessageText>
                )
              }
            }

            return (
              <StyledMessage
                id={`message-${index}`}
                key={`message-${index}`}
                sender={sender}>
                {prevAuthor !== sender && (
                  <Author sender={sender}>
                    {sender === "user" ? identity.userName : identity.agentName}
                  </Author>
                )}
                {isThinking && (
                  <Thinking>
                    <DotFlashingAnim />
                    Thinking...
                  </Thinking>
                )}
                <MessageBubble
                  isThinking={delayedThinking[index]}
                  type={metadata?.type}>
                  {!isThinking && renderContent()}
                </MessageBubble>
              </StyledMessage>
            )
          })}
          <Spacer height={{ default: "100px" }} />
        </Content>
      </ChatWrapper>
    </ChatContainer>
  )
}
